// ---------------------------------------------------------------------------
// The Overview: the page people land on after signing in (R15b)
//
// Rebuilt from the design system's ConsoleOverview and the ports, in the
// system's order (docs/design/system/README.md, Layout): what needs a person,
// how it is going, then the work.
//   - The PageHeader: "Overview", the counts in one line, "New project" and
//     the one primary action "New campaign" last (R12d d4: on a desktop it is
//     only here; a phone also has the bar's "+"). Inside a project: a back
//     link "Overview" (one level down), the project's name, its counts and
//     its description. The title is the page's one h1, tabindex -1, which the
//     router focuses after a page change.
//   - The one Callout, critical, right under the header, when a list could
//     not be loaded (R15a's failure, failureCallout in dashboard-load.ts):
//     the figures give way without the campaigns, the grid without the
//     projects.
//   - Four MetricTiles without a comparison (7.9; dashboard-figures.ts, the
//     third R15c's "Leads contacted"), or their skeletons while loading.
//   - The projects as a grid of Panels (7.9: the grid stays): the name, its
//     campaign count, its description, Edit and Delete (never for the default
//     project, whose name the page writes and the server will not delete),
//     and "Show campaigns", a real button, so a keyboard opens a project as a
//     mouse does with a click anywhere on the panel. The focus follows: into
//     a project to its title, back to the button it came from. No projects:
//     the system's EmptyState.
//   - Inside a project: its campaigns, in the system's DataTable in a flush
//     Panel "Campaigns" (R15d, dashboard-campaigns.ts): sorted from the
//     headers, rows that open from the keyboard too, the StatusBadge, the
//     empty states as the system's EmptyState. Its actions are drawn here,
//     because they hold the page's state: Edit, Archive or Unarchive, and
//     Delete, small ghost icon buttons named for the campaign. While one of
//     a row's requests runs, every action of that row is disabled and the
//     one that runs spins (R15d: they were only greyed, so a second press
//     sent again). The whole page is on the system's 14/22 base, with no
//     legacy island (shell/outlet.ts leaves the Overview out of the frame's).
//   - The project dialog is the system's Dialog: Name (the fix said at the
//     field when it is empty, nothing sent), Description (optional), Cancel
//     and Create project or Save, busy while it saves; editing, Delete
//     project first. Its delete question (confirm-dialog.ts, until R14) opens
//     with the dialog stepped aside: two modals' focus traps would fight over
//     the focus, and the dialog's took the question's. Cancel brings the
//     dialog back as it was.
// No activity column: there is no source for one (7.9). Only data the API
// gives today: GET /api/campaigns and GET /api/projects.
//
// Layout only through Tailwind (7.2), on the page as an @container, so the
// grids follow the page's width and not the window's (the SideNav takes 240px
// from 768px): one column on a phone, two tiles from 448px and two panels
// from 576px, four tiles and three panels from 896px. The page's gutter is
// 16px on a phone and the system's 32px from 768px, as ConsoleOverview's.
//
// The page's state lives in a PageHost (page-host.ts, R16b), not in
// functional-lit; it loads both lists once it is in the document. Every busy
// flag is released in a finally (R15a), every failure is said in words that
// say what to do (7.5) and written to the console under "[dashboard]".
//
// An answer changes the page as it is when the answer comes, not as it was
// when its request left (R15d, and the R15b review for the projects): two
// actions on two rows used to write back each its own old copy, so a
// campaign deleted while another one archived came back into the list, and
// two project deletes brought the first project back. The campaigns and the
// busy rows, the projects, the project deletes on the way (one per project,
// each busy until its own answer), the open project and the open dialog are
// kept in `held`, which every change reads and writes, and the state is set
// from it. A project deleted while it is open on the page takes the page back
// to the projects; a delete closes only its own project's dialog; when the
// deleted project's panel or a campaign's row took the focus with it, the
// title gets it.
// ---------------------------------------------------------------------------

import { html, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

import { confirmDialog, confirmDialogWithCheckbox } from '../confirm-dialog.js';
import { button } from '../design/button.js';
import type { SdrDialog } from '../design/dialog.js';
import '../design/dialog.js';
import '../design/feedback.js';
import { skeleton } from '../design/feedback.js';
import { textField } from '../design/field.js';
import { pageHeader } from '../design/page-header.js';
import { panel } from '../design/panel.js';
import '../design/text-area.js';
import { sdrRouter } from '../router/app-router.js';
import { toast } from '../toast.js';
import { campaignName, campaignsPanel } from './dashboard-campaigns.js';
import { formatCount, overviewFigures, overviewTiles, overviewTilesLoading } from './dashboard-figures.js';
import { failureCallout, loadOverview, NO_CONNECTION, overviewFailure } from './dashboard-load.js';
import { definePage, type PageHost, type PageSpec } from './page-host.js';

/**
 * How long a deleted campaign stays restorable. Mirrors
 * CAMPAIGN_RETENTION_DAYS in the server's helpers/campaign-lifecycle.ts —
 * duplicated because the confirmation dialog has to state the window before
 * any request is made, and a promise the user reads must not be a guess.
 */
const RETENTION_DAYS = 30;

/** The prefix of the page's console lines, as R15c's (dashboard-load.ts). */
const LOG_PREFIX = '[dashboard]';

/** Said at the name field when it is empty: the fix, not the fault (7.5). */
export const NAME_MISSING = 'Enter a name for the project.';

interface CampaignSummary {
  _id: string;
  name: string;
  campaignType: string;
  status: string;
  leadsProcessed?: number;
  leadsFailed?: number;
  totalSent?: number;
  /** Leads on the Do Not Contact list, computed live server-side (campaign-live-counts.ts). */
  totalOptedOut?: number;
  totalReplied?: number;
  totalBounced?: number;
  createdAt: string;
  updatedAt: string;
  launchedAt?: string;
  /** Set while the campaign is archived; cleared on unarchive. */
  archivedAt?: string;
  projectId?: string;
}

interface ProjectSummary {
  _id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
}

/** What the page calls a project: the default one by its role, the others by their name. */
const projectName = (p: ProjectSummary) => (p.isDefault ? 'Default project' : p.name);

/** The id of a project's "Show campaigns" button, where the focus goes back to from inside it. */
const openButtonId = (p: ProjectSummary) => `overview-open-${p._id}`;
const TITLE_ID = 'overview-title';

/** A failure in the console, under the page's prefix: the field sees what people were told. */
const warn = (what: string, reason: unknown) => console.warn(`${LOG_PREFIX} ${what} failed: ${String(reason)}`);

/** A request a campaign's row has on the way. */
type RowAction = 'archive' | 'unarchive' | 'delete';

/** What every answer changes, as it is now, whenever the answer comes (R15d; the projects: R15b review). */
interface Held {
  campaigns: CampaignSummary[];
  busy: Readonly<Record<string, RowAction>>;
  projects: ProjectSummary[];
  /** The projects whose delete is on the way: each one's Delete waits for its own answer. */
  deletingProjects: Readonly<Record<string, true>>;
  selectedProjectId: string | null;
  editingProject: ProjectSummary | 'new' | null;
}

/**
 * Loads the lists once the page is in the document, as functional-lit's useEffect(…, []) did: in a microtask
 * after the first render, whose view hands it the page's own load (a setter called while the page renders
 * would throw, page-host.ts). A page left before that loads nothing.
 */
class OpenLoad {
  #load: (() => void) | undefined;
  #started = false;
  readonly #host: PageHost;

  constructor(host: PageHost) {
    this.#host = host;
    host.addController(this);
  }

  /** The page's load, from the render running now. */
  use(load: () => void): void {
    this.#load = load;
  }

  hostConnected(): void {
    if (this.#started) return;
    this.#started = true;
    queueMicrotask(() => {
      if (this.#host.mounted) this.#load?.();
    });
  }
}

const OVERVIEW: PageSpec<Record<string, never>, OpenLoad> = { name: LOG_PREFIX, create: host => new OpenLoad(host) };

const OverviewPage = definePage(OVERVIEW, (page, opening) => {
  const [campaigns, setCampaignsState] = page.state<CampaignSummary[]>('campaigns', []);
  // Campaign ids with a request on the way, and which one: that row's actions wait, the one that runs spins.
  const [busyRows, setBusyRows] = page.state<Readonly<Record<string, RowAction>>>('busyRows', {});
  // What an answer changes: the lists, the busy rows and deletes, the open project and dialog as they are
  // when it comes, not as the render that sent its request saw them (R15d, R15b review). Every write goes
  // through here, so `held` is always the latest.
  const [held] = page.state<Held>('held', {
    campaigns: [],
    busy: {},
    projects: [],
    deletingProjects: {},
    selectedProjectId: null,
    editingProject: null,
  });
  const setCampaigns = (next: CampaignSummary[]) => {
    held.campaigns = next;
    setCampaignsState(next);
  };
  const changeCampaigns = (change: (now: CampaignSummary[]) => CampaignSummary[]) =>
    setCampaigns(change(held.campaigns));
  const setRowBusy = (id: string, action: RowAction | null) => {
    const { [id]: _, ...others } = held.busy;
    held.busy = action ? { ...others, [id]: action } : others;
    setBusyRows(held.busy);
  };
  const [loading, setLoading] = page.state('loading', true);
  // Why the campaigns or the projects could not be loaded (null: they were).
  // The page says so where the figures and the grid would be, never as zeros.
  const [campaignsError, setCampaignsError] = page.state<string | null>('campaignsError', null);
  const [projectsError, setProjectsError] = page.state<string | null>('projectsError', null);
  // The projects, the open project and the dialog go through `held` too (R15b review).
  const [projects, setProjectsState] = page.state<ProjectSummary[]>('projects', []);
  const setProjects = (next: ProjectSummary[]) => {
    held.projects = next;
    setProjectsState(next);
  };
  const changeProjects = (change: (now: ProjectSummary[]) => ProjectSummary[]) => setProjects(change(held.projects));
  // null = showing the Projects grid; set = drilled into one project's campaigns.
  const [selectedProjectId, setSelectedState] = page.state<string | null>('selectedProjectId', null);
  const setSelectedProjectId = (id: string | null) => {
    held.selectedProjectId = id;
    setSelectedState(id);
  };
  // The name/description dialog, shared by both flows: a ProjectSummary means
  // "edit this one", the 'new' sentinel means "create one". One dialog rather
  // than two near-identical copies that would drift apart.
  const [editingProject, setEditingState] = page.state<ProjectSummary | 'new' | null>('editingProject', null);
  const setEditingProject = (p: ProjectSummary | 'new' | null) => {
    held.editingProject = p;
    setEditingState(p);
  };
  const [projName, setProjName] = page.state('projName', '');
  const [projDescription, setProjDescription] = page.state('projDescription', '');
  // The fix for the name, at the field (7.5), until something is typed.
  const [nameError, setNameError] = page.state('nameError', '');
  const [savingProject, setSavingProject] = page.state('savingProject', false);
  // The dialog stepped aside while its delete question is open (and its delete runs).
  const [askingDelete, setAskingDelete] = page.state('askingDelete', false);
  const [dialogRef] = page.state('dialogRef', createRef<SdrDialog>());
  // Archived campaigns are kept out of the list by default — that's the point
  // of archiving — but stay one click away rather than hidden outright.
  const [showArchived, setShowArchived] = page.state('showArchived', false);
  // The projects whose delete request is in flight: each one's Delete is busy until its own answer.
  const [deletingProjects, setDeletingProjects] = page.state<Readonly<Record<string, true>>>('deletingProjects', {});
  const setProjectDeleting = (id: string, on: boolean) => {
    const { [id]: _, ...others } = held.deletingProjects;
    held.deletingProjects = on ? { ...others, [id]: true } : others;
    setDeletingProjects(held.deletingProjects);
  };

  // Both lists together: the figures, the subtitle and the grid wait for
  // both, so none of them shows a count the other list has not answered yet.
  // loadOverview hands each answer to these setters, and a failure never as
  // an empty list (dashboard-load.ts, tested there by behaviour).
  const load = async () => {
    setLoading(true);
    try {
      await loadOverview<CampaignSummary, ProjectSummary>({
        setCampaigns,
        setCampaignsError,
        setProjects,
        setProjectsError,
      });
    } finally {
      setLoading(false);
    }
  };
  opening.use(() => void load());

  const defaultProjectId = projects.find(p => p.isDefault)?._id;
  const effectiveProjectId = (c: CampaignSummary): string | undefined => c.projectId ?? defaultProjectId;
  const sortedProjects = projects
    .slice()
    .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : a.name.localeCompare(b.name)));
  const campaignsForProject = (projectId: string) => campaigns.filter(c => effectiveProjectId(c) === projectId);
  const selectedProject = selectedProjectId ? projects.find(p => p._id === selectedProjectId) : null;
  // The tiles still count archived campaigns — they exist and their numbers
  // are real. Only the list hides them (dashboard-campaigns.ts).
  const scopedCampaigns = selectedProjectId ? campaignsForProject(selectedProjectId) : campaigns;

  /** Once the page has drawn what was just set: moves the focus to the element with `id`. */
  const focusAfter = (id: string) => {
    void page.updateComplete.then(drawn => {
      if (drawn) document.getElementById(id)?.focus();
    });
  };
  /**
   * Once the page has drawn: the title takes the focus if what had it went with the change (a deleted project's
   * panel, a campaign's row), else it stays where it is (R15b and R15d review).
   */
  const keepFocus = () => {
    void page.updateComplete.then(drawn => {
      const at = document.activeElement;
      if (drawn && (!at || at === document.body || !at.isConnected)) document.getElementById(TITLE_ID)?.focus();
    });
  };

  // Into a project: its title takes the focus, the button that was pressed is gone. Back: the focus
  // returns to the "Show campaigns" of the project it came from.
  const openProject = (p: ProjectSummary) => {
    setSelectedProjectId(p._id);
    focusAfter(TITLE_ID);
  };
  const backToProjects = () => {
    const from = selectedProject;
    setSelectedProjectId(null);
    if (from) focusAfter(openButtonId(from));
  };

  const startEditProject = (p: ProjectSummary) => {
    setProjName(p.name ?? '');
    setProjDescription(p.description ?? '');
    setNameError('');
    setEditingProject(p);
  };

  const startNewProject = () => {
    setProjName('');
    setProjDescription('');
    setNameError('');
    setEditingProject('new');
  };

  const closeDialog = () => {
    setEditingProject(null);
    setNameError('');
  };

  /**
   * Deletes the grouping, never the campaigns. Everything inside moves to the
   * default project — which is safe by construction, since the default project
   * is the one place duplicate leads across campaigns are allowed. From the
   * dialog, the dialog steps aside while the question is open and the delete
   * runs: its focus trap would take the question's focus.
   */
  const deleteProject = async (e: Event, p: ProjectSummary, fromDialog = false) => {
    e.stopPropagation();
    // One delete per project: while it runs its Delete is disabled, and a press that comes first does nothing.
    if (held.deletingProjects[p._id]) return;
    const count = campaignsForProject(p._id).length;
    if (fromDialog) {
      // Closed before the question opens, so the dialog has given the focus back to what opened it: the
      // question returns it there, and the dialog, coming back on Cancel, takes it from there again.
      setAskingDelete(true);
      await page.updateComplete;
      await dialogRef.value?.updateComplete;
    }
    try {
      if (
        !(await confirmDialog(
          count === 0
            ? `Delete the project “${p.name}”? It has no campaigns in it.`
            : `Delete the project “${p.name}”? Its ${count} campaign${count === 1 ? '' : 's'} ${
                count === 1 ? 'is' : 'are'
              } not deleted — ${count === 1 ? 'it moves' : 'they move'} to the default project. Only the grouping is lost.`,
          { title: 'Delete project', confirmLabel: 'Delete project', confirmClass: 'btn-error' },
        ))
      )
        return;
      setProjectDeleting(p._id, true);
      try {
        const resp = await fetch(`/api/projects/${encodeURIComponent(p._id)}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        if (!resp.ok || !data?.ok) {
          warn(`deleting project ${p._id}`, data?.error ?? `HTTP ${resp.status}`);
          toast.error(data?.error ?? 'The project was not deleted. Try again.');
          return;
        }
        // Re-point the moved campaigns locally so the default project's count is
        // right immediately, without a full reload.
        const newProjectId = data.defaultProjectId ?? defaultProjectId;
        changeCampaigns(now => now.map(c => (effectiveProjectId(c) === p._id ? { ...c, projectId: newProjectId } : c)));
        changeProjects(now => now.filter(x => x._id !== p._id));
        // Inside the project that is gone (it was opened while its delete ran):
        // there's nothing left to look at — go back to the grid.
        if (held.selectedProjectId === p._id) setSelectedProjectId(null);
        // Deleting can also be triggered from inside the edit dialog; that
        // dialog is now editing something that no longer exists, so close it.
        // Another project's dialog, opened meanwhile, stays as it is.
        const open = held.editingProject;
        if (open && open !== 'new' && open._id === p._id) setEditingProject(null);
        // The panel and its buttons are gone, the focus with them: to the title.
        keepFocus();
        toast.success(
          data.movedCampaigns > 0
            ? `Project deleted — ${data.movedCampaigns} campaign${
                data.movedCampaigns === 1 ? '' : 's'
              } moved to the default project.`
            : 'Project deleted.',
        );
      } catch (error) {
        warn(`deleting project ${p._id}`, error);
        toast.error(`The project was not deleted. ${NO_CONNECTION}`);
      } finally {
        setProjectDeleting(p._id, false);
      }
    } finally {
      // Back once the key that answered the question has gone its way (found in Chrome): an Escape is still on
      // its way through the document when the question settles, and Chrome runs the page's update before it goes
      // on, so the dialog, open again and listening, took the same Escape and closed as well. Cancel came back.
      if (fromDialog) await new Promise(resolve => setTimeout(resolve, 0));
      if (fromDialog) setAskingDelete(false);
    }
  };

  const saveProject = async () => {
    if (!editingProject) return;
    if (!projName.trim()) {
      setNameError(NAME_MISSING);
      focusAfter('project-name');
      return;
    }
    const isNew = editingProject === 'new';
    const what = isNew ? 'creating a project' : `saving project ${editingProject._id}`;
    setSavingProject(true);
    try {
      const body = JSON.stringify({ name: projName.trim(), description: projDescription.trim() });
      const resp = isNew
        ? await fetch('/api/projects', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body })
        : await fetch(`/api/projects/${encodeURIComponent(editingProject._id)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
      const data = await resp.json().catch(() => null);
      if (!data?.ok) {
        warn(what, data?.error ?? `HTTP ${resp.status}`);
        toast.error(
          data?.error ?? (isNew ? 'The project was not created. Try again.' : 'The project was not saved. Try again.'),
        );
        return;
      }
      // Into the list as it is now: a project deleted meanwhile stays gone, one created meanwhile stays.
      const saved = editingProject;
      changeProjects(now =>
        saved === 'new' ? [...now, data.project] : now.map(p => (p._id === saved._id ? { ...p, ...data.project } : p)),
      );
      // The dialog that was saved closes, if it is still the one open.
      if (held.editingProject === saved) setEditingProject(null);
      toast.success(isNew ? 'Project created.' : 'Project saved.');
    } catch (error) {
      warn(what, error);
      toast.error(`${isNew ? 'The project was not created.' : 'The project was not saved.'} ${NO_CONNECTION}`);
    } finally {
      // Here, not after the try: the ok:false branch returns early, and left
      // Create/Save disabled in every project's dialog until a reload.
      setSavingProject(false);
    }
  };

  // ── The campaigns inside a project (R15d) ────────────────────────────────

  const handleCampaignClick = (c: CampaignSummary) => {
    const id = typeof c._id === 'string' ? c._id.trim() : '';
    if (!id) {
      console.warn('[dashboard] Skipping campaign click — missing _id', c);
      return;
    }
    sdrRouter.goTo('campaignDetail', { params: { campaignId: id } });
  };

  const editCampaign = (c: CampaignSummary) => {
    const id = typeof c._id === 'string' ? c._id.trim() : '';
    if (!id) return;
    sdrRouter.goTo('editCampaign', { params: { campaignId: id } });
  };

  // Mirrors the gate on campaign-detail.ts's own Edit button — active/
  // archived/error campaigns don't show it here either, so nobody can jump
  // into the wizard by accident on a campaign that's already sending.
  // 'paused' IS included so the Leads step's top-up (CSV/HubSpot/Apollo) is
  // reachable while paused — other wizard steps still reject edits there.
  const canEditFromDashboard = (status: string | undefined) =>
    status === 'draft' || status === 'ready_to_launch' || status === 'preparing_to_launch' || status === 'paused';

  // Archiving is offered wherever the campaign detail page offers it, so the
  // two views can't disagree about what's archivable.
  const canArchive = (status: string | undefined) => status !== 'archived' && status !== 'draft';

  /**
   * Delete = soft delete. The campaign leaves the dashboard and stops sending,
   * but it is recoverable from Settings › Deleted Campaigns for
   * RETENTION_DAYS. The dialog says so — "cannot be undone" was the old copy
   * and is now simply untrue.
   */
  const deleteCampaign = async (c: CampaignSummary) => {
    if (held.busy[c._id]) return;
    const label = c.name || 'this untitled draft';
    const canRecall = c.campaignType === 'linkedin' || c.campaignType === 'multi';
    const message = `Delete “${label}”? It stops sending and disappears from your dashboard. You can restore it for ${RETENTION_DAYS} days under Settings › Deleted Campaigns — after that it is deleted permanently.`;
    let recall = false;
    if (canRecall) {
      const res = await confirmDialogWithCheckbox(message, {
        title: 'Delete campaign',
        confirmLabel: 'Delete campaign',
        confirmClass: 'btn-error',
        checkboxLabel:
          'Also recall this campaign’s connection requests — cancel the pending ones and withdraw its already-sent invitations on LinkedIn. This can’t be undone by Restore.',
      });
      if (!res.confirmed) return;
      recall = res.checked;
    } else if (
      !(await confirmDialog(message, {
        title: 'Delete campaign',
        confirmLabel: 'Delete campaign',
        confirmClass: 'btn-error',
      }))
    ) {
      return;
    }
    const what = `deleting campaign ${c._id}`;
    setRowBusy(c._id, 'delete');
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(c._id)}${recall ? '?recallInvitations=true' : ''}`,
        { method: 'DELETE' },
      );
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        warn(what, data?.error ?? `HTTP ${resp.status}`);
        toast.error(data?.error ?? 'The campaign was not deleted. Try again.');
        return;
      }
      changeCampaigns(now => now.filter(x => x._id !== c._id));
      // The row is gone, and the focused button with it: to the title (review).
      keepFocus();
      toast.success(`Campaign deleted — restorable for ${data?.retentionDays ?? RETENTION_DAYS} days in Settings.`);
      if (recall && data?.recalled) {
        const r = data.recalled;
        toast.info(`Connection requests recalled: ${r.sentRecalled} withdrawn, ${r.jobsCancelled} pending cancelled.`);
      }
      if (data?.warning) {
        toast.warning(data.warning);
      }
    } catch (error) {
      warn(what, error);
      toast.error(`The campaign was not deleted. ${NO_CONNECTION}`);
    } finally {
      setRowBusy(c._id, null);
    }
  };

  const archiveCampaign = async (c: CampaignSummary) => {
    if (held.busy[c._id]) return;
    const label = c.name || 'this untitled draft';
    if (
      !(await confirmDialog(
        `Archive “${label}”? It stops sending and moves out of your campaign list. Nothing is deleted — you can unarchive it at any time.`,
        { title: 'Archive campaign', confirmLabel: 'Archive', confirmClass: 'btn-warning' },
      ))
    )
      return;
    const what = `archiving campaign ${c._id}`;
    setRowBusy(c._id, 'archive');
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(c._id)}/archive`, { method: 'POST' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        warn(what, data?.error ?? `HTTP ${resp.status}`);
        toast.error(data?.error ?? 'The campaign was not archived. Try again.');
        return;
      }
      changeCampaigns(now => now.map(x => (x._id === c._id ? { ...x, status: 'archived' } : x)));
      // Hidden with the archived ones, the row takes the focused button with it: to the title (review).
      keepFocus();
      toast.success('Campaign archived.');
      if (data.warning) toast.warning(data.warning);
    } catch (error) {
      warn(what, error);
      toast.error(`The campaign was not archived. ${NO_CONNECTION}`);
    } finally {
      setRowBusy(c._id, null);
    }
  };

  // No confirmation: unarchiving destroys nothing and the campaign comes back
  // paused, so the worst case is one more click to re-archive it.
  const unarchiveCampaign = async (c: CampaignSummary) => {
    if (held.busy[c._id]) return;
    const what = `unarchiving campaign ${c._id}`;
    setRowBusy(c._id, 'unarchive');
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(c._id)}/unarchive`, { method: 'POST' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        warn(what, data?.error ?? `HTTP ${resp.status}`);
        toast.error(data?.error ?? 'The campaign is still archived. Try again.');
        return;
      }
      changeCampaigns(now => now.map(x => (x._id === c._id ? { ...x, status: data.status ?? 'paused' } : x)));
      toast.success(
        data.status === 'paused'
          ? 'Campaign unarchived — it is paused, resume it when you want it sending again.'
          : 'Campaign unarchived.',
      );
    } catch (error) {
      warn(what, error);
      toast.error(`The campaign is still archived. ${NO_CONNECTION}`);
    } finally {
      setRowBusy(c._id, null);
    }
  };

  /**
   * A row's actions: small ghost icon buttons named for the campaign. While one of the row's requests runs, all of
   * them wait and the one that runs spins (the Button port's loading: disabled, aria-busy). A click in here is the
   * buttons' and never opens the row, also on a button that is disabled and so calls nothing.
   */
  const rowActions = (c: CampaignSummary) => {
    const name = campaignName(c);
    const running = busyRows[c._id];
    const waiting = running !== undefined;
    const action = (icon: 'edit' | 'archive' | 'unarchive' | 'delete', verb: string, title: string, run: () => void) =>
      button({
        variant: 'ghost',
        size: 'sm',
        icon,
        ariaLabel: `${verb} campaign ${name}`,
        title,
        loading: running === icon,
        disabled: waiting,
        onClick: run,
      });
    return html`<span class="inline-flex gap-1" @click=${(e: Event) => e.stopPropagation()}
      >${canEditFromDashboard(c.status) ? action('edit', 'Edit', 'Edit campaign', () => editCampaign(c)) : nothing}${
        c.status === 'archived'
          ? action('unarchive', 'Unarchive', 'Unarchive campaign', () => void unarchiveCampaign(c))
          : canArchive(c.status)
            ? action(
                'archive',
                'Archive',
                'Archive campaign — keeps it, just out of the way',
                () => void archiveCampaign(c),
              )
            : nothing
      }${action('delete', 'Delete', `Delete campaign — restorable for ${RETENTION_DAYS} days`, () => void deleteCampaign(c))}</span
    >`;
  };

  const activeCount = scopedCampaigns.filter(c => c.status === 'active').length;
  const plural = (n: number, word: string) => `${formatCount(n)} ${word}${n === 1 ? '' : 's'}`;
  // Only counts the server sent: without the projects, no "in 0 projects".
  const subtitle = selectedProject
    ? `${plural(scopedCampaigns.length, 'campaign')} · ${formatCount(activeCount)} active`
    : projectsError
      ? `${plural(campaigns.length, 'campaign')} · ${formatCount(activeCount)} active`
      : `${plural(campaigns.length, 'campaign')} in ${plural(projects.length, 'project')} · ${formatCount(activeCount)} active`;
  // What could not be loaded, where the grid or the project's campaigns would be.
  const failure = overviewFailure({ campaignsError, projectsError, inProject: selectedProjectId !== null });

  // ── The page, on the system ──────────────────────────────────────────────

  const newCampaign = button(
    { variant: 'primary', icon: 'plus', onClick: () => sdrRouter.goTo('newCampaign') },
    'New campaign',
  );
  // A new project goes into the list the page holds: not offered while that list could not be loaded.
  const newProject = button({ icon: 'plus', onClick: startNewProject }, 'New project');
  const counted = !loading && !campaignsError;

  const head = selectedProject
    ? pageHeader({
        titleId: TITLE_ID,
        back: { label: 'Overview', onClick: backToProjects },
        title: projectName(selectedProject),
        description:
          counted || selectedProject.description
            ? html`${counted ? subtitle : nothing} ${
                selectedProject.description
                  ? html`<span class="block max-w-2xl">${selectedProject.description}</span>`
                  : nothing
              }`
            : undefined,
        primaryAction: newCampaign,
      })
    : pageHeader({
        titleId: TITLE_ID,
        title: 'Overview',
        description: counted ? subtitle : undefined,
        actions: loading || !projectsError ? newProject : undefined,
        primaryAction: newCampaign,
      });

  const projectPanel = (p: ProjectSummary) => {
    const name = projectName(p);
    const count = campaignsForProject(p._id).length;
    // The default project is a system container whose name the page always
    // writes as "Default project" — renaming it would have no visible effect,
    // so it isn't offered. Deleting it is refused server-side for the same
    // reason: it is where campaigns without a project live.
    const actions = p.isDefault
      ? undefined
      : html`${button({
          variant: 'ghost',
          size: 'sm',
          icon: 'edit',
          ariaLabel: `Edit project ${p.name}`,
          title: 'Edit name and description',
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
            startEditProject(p);
          },
        })}${button({
          variant: 'ghost',
          size: 'sm',
          icon: 'delete',
          ariaLabel: `Delete project ${p.name}`,
          title: 'Delete project: its campaigns move to the default project',
          loading: deletingProjects[p._id] === true,
          onClick: (e: MouseEvent) => void deleteProject(e, p),
        })}`;
    // A click anywhere on the panel but on a control opens the project, as the card did; the button is the
    // way in for a keyboard and a screen reader.
    return html`<div
      class="grid cursor-pointer"
      @click=${(e: MouseEvent) => {
        if (!(e.target as Element).closest('button, a')) openProject(p);
      }}
    >
      ${panel(
        { title: name, subtitle: plural(count, 'campaign'), actions },
        html`<div class="flex flex-col items-start gap-3">
          ${p.description ? html`<p class="line-clamp-2">${p.description}</p>` : nothing}
          <div class="-ml-3">
            ${button(
              {
                variant: 'ghost',
                size: 'sm',
                iconRight: 'chevron-right',
                id: openButtonId(p),
                ariaLabel: `Show campaigns in ${name}`,
                onClick: () => openProject(p),
              },
              'Show campaigns',
            )}
          </div>
        </div>`,
      )}
    </div>`;
  };

  const PANEL_GRID = 'grid grid-cols-1 gap-6 @xl:grid-cols-2 @4xl:grid-cols-3';

  const work = () => {
    if (loading) {
      return html`<div class=${PANEL_GRID} aria-busy="true">
        ${panel({}, skeleton({ variant: 'text', lines: 3, label: 'Loading the projects' }))}
        ${panel({}, skeleton({ variant: 'text', lines: 3 }))} ${panel({}, skeleton({ variant: 'text', lines: 3 }))}
      </div>`;
    }
    if (failure) return nothing;
    if (selectedProjectId !== null) {
      return campaignsPanel({
        campaigns: scopedCampaigns,
        showArchived,
        onToggleArchived: () => setShowArchived(!showArchived),
        onOpen: handleCampaignClick,
        onNewCampaign: () => sdrRouter.goTo('newCampaign'),
        actions: rowActions,
      });
    }
    if (sortedProjects.length === 0) {
      return html`<sdr-empty-state
        .variant=${'first-run'}
        .level=${2}
        .heading=${'No projects yet'}
        .content=${'A project groups campaigns. Create the first one, then start a campaign in it.'}
        .action=${button({ variant: 'primary', icon: 'plus', onClick: startNewProject }, 'New project')}
      ></sdr-empty-state>`;
    }
    // Keyed by project (found in Chrome): unkeyed, the next project's panel took over a deleted one's elements, and
    // a focus left in the deleted panel landed on another project's button instead of going to the title.
    return html`<div class=${PANEL_GRID}>${repeat(sortedProjects, p => p._id, projectPanel)}</div>`;
  };

  // ── The project dialog ───────────────────────────────────────────────────

  const editing = editingProject && editingProject !== 'new' ? editingProject : null;
  const dialogBody = html`<div class="flex flex-col gap-4">
    ${textField({
      id: 'project-name',
      label: 'Name',
      value: projName,
      placeholder: 'e.g. Logistics DACH',
      error: nameError || undefined,
      onInput: (e: InputEvent) => {
        setProjName((e.target as HTMLInputElement).value);
        if (nameError) setNameError('');
      },
    })}
    <sdr-text-area
      .label=${'Description'}
      .optional=${true}
      .value=${projDescription}
      .placeholder=${'e.g. Freight forwarders in DACH with more than 50 people'}
      .minRows=${3}
      .maxRows=${8}
      .onChange=${(e: Event) => setProjDescription((e.target as HTMLTextAreaElement).value)}
    ></sdr-text-area>
  </div>`;
  const dialogFooter = html`${
    // Delete lives here too, not only on the panel: the edit dialog is where
    // people go when they want to *change* a project, and "get rid of it" is
    // the same intent. Set apart from Cancel and Save, first, so it can't be
    // hit by reflex — and never offered for the default project, which the
    // server refuses to delete anyway.
    editing && !editing.isDefault
      ? html`<span class="mr-auto"
          >${button(
            {
              variant: 'danger',
              icon: 'delete',
              loading: deletingProjects[editing._id] === true,
              onClick: (e: MouseEvent) => void deleteProject(e, editing, true),
            },
            'Delete project',
          )}</span
        >`
      : nothing
  }${button({ onClick: closeDialog }, 'Cancel')}${button(
    { variant: 'primary', loading: savingProject, onClick: () => void saveProject() },
    editingProject === 'new' ? 'Create project' : 'Save',
  )}`;

  return html`<div class="@container flex flex-col gap-6 px-4 py-6 md:px-8 md:pt-8 md:pb-6">
    ${head}
    ${loading || !failure ? nothing : failureCallout(failure.title, failure.reason, () => void load())}
    ${loading ? overviewTilesLoading() : campaignsError ? nothing : overviewTiles(overviewFigures(scopedCampaigns))}
    ${work()}
    <sdr-dialog
      ${ref(dialogRef)}
      .open=${editingProject !== null && !askingDelete}
      .heading=${editingProject === 'new' ? 'New project' : 'Edit project'}
      .content=${editingProject ? dialogBody : nothing}
      .footer=${editingProject ? dialogFooter : nothing}
      .onClose=${closeDialog}
    ></sdr-dialog>
  </div>`;
});

/** The Overview, as the router's map renders it. */
export const Dashboard = (): unknown => OverviewPage({});
